import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import i18n from "../i18n";
import { dayKey, formatTimeShort, isSameDay, weekdayLabels } from "../utils/calendarDates";
import { CELL_HOVER, WEEKEND_TINT, typeStyle } from "../utils/calendarEvents";

const CHIP_LIMIT = 3;

/**
 * Month view. Always six weeks tall (see visibleDays): a grid that switches
 * between five and six rows reflows the whole page on every next-month press,
 * which is the single most noticeable jitter in a calendar.
 */
export default function CalendarMonthGrid({
  days, cursor, eventsByDay, today,
  onOpenDay, onCreateAt, onOpenEvent,
  canDrag, dragging, onDragStart, onDragEnd, onDropOnDay,
  writable,
}) {
  const { t } = useTranslation();
  const [dropTarget, setDropTarget] = useState(null);
  const weekdays = weekdayLabels(i18n.language);
  const month = cursor.getMonth();

  return (
    <div
      data-testid="calendar-grid"
      role="grid"
      aria-label={t("calendar.title")}
      className="border border-border rounded-sm overflow-hidden bg-bg"
    >
      <div role="row" className="grid grid-cols-7 border-b border-border bg-surface">
        {weekdays.map((w, i) => (
          <div
            key={w}
            role="columnheader"
            className={`text-center text-[11px] font-medium uppercase tracking-[0.12em] py-2 capitalize ${
              i > 4 ? "text-muted opacity-70" : "text-muted"
            }`}
          >
            {w}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {days.map((date, i) => {
          const key = dayKey(date);
          const events = eventsByDay.get(key) || [];
          const isToday = isSameDay(date, today);
          const outside = date.getMonth() !== month;
          const weekend = date.getDay() === 0 || date.getDay() === 6;
          const isDropTarget = dropTarget === key;
          const overflow = events.length - CHIP_LIMIT;

          return (
            // The cell's click is a pointer shortcut for "create on this day".
            // It carries no keyboard handler on purpose: the equivalent path is
            // the day-number button (opens the day) and the toolbar's New
            // button, whose date field is prefilled -- 42 more tab stops per
            // month would cost a keyboard user more than they'd gain.
            <div
              key={key}
              role="gridcell"
              data-testid={`cal-day-${date.getDate()}`}
              onClick={() => writable && onCreateAt(date, null)}
              onDragOver={canDrag && dragging ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDropTarget(key); } : undefined}
              onDragLeave={canDrag ? () => setDropTarget((prev) => (prev === key ? null : prev)) : undefined}
              onDrop={canDrag && dragging ? (e) => { e.preventDefault(); setDropTarget(null); onDropOnDay(date); } : undefined}
              className={`group relative min-h-[76px] sm:min-h-[104px] p-1.5 flex flex-col gap-1 text-left border-border transition-colors duration-150
                ${i % 7 !== 6 ? "border-r" : ""} ${i < 35 ? "border-b" : ""}
                ${outside ? "bg-surface" : weekend ? WEEKEND_TINT : ""}
                ${isDropTarget ? "bg-primary/10 ring-1 ring-inset ring-primary/50" : ""}
                ${writable ? `cursor-pointer ${CELL_HOVER}` : ""}`}
            >
              <div className="flex items-center justify-between gap-1">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onOpenDay(date); }}
                  aria-label={`${date.toLocaleDateString(i18n.language, { weekday: "long", day: "numeric", month: "long" })} — ${t("calendar.eventCount", { count: events.length })}`}
                  className={`shrink-0 min-w-6 h-6 px-1 flex items-center justify-center rounded-sm text-xs tabular-nums outline-none transition-colors duration-150
                    focus-visible:ring-2 focus-visible:ring-primary/60
                    ${isToday
                      ? "bg-primary text-white font-bold"
                      : outside
                        ? "text-muted opacity-60 hover:text-txt"
                        : "text-muted hover:bg-border hover:text-txt"}`}
                >
                  {date.getDate()}
                </button>
                {writable && (
                  // Mouse affordance only. The keyboard path to "create on a
                  // given day" is the toolbar's New button, whose date field
                  // is prefilled from the day in focus -- 42 extra tab stops
                  // in the grid would cost more than they give back.
                  <span
                    aria-hidden="true"
                    className="opacity-0 group-hover:opacity-100 transition-opacity duration-150 text-muted"
                  >
                    <Plus size={13} />
                  </span>
                )}
              </div>

              {/* Chips on sm+; on a phone a cell only has room for the shape of
                  the day, so it degrades to one dot per event. */}
              <div className="hidden sm:flex flex-col gap-1 min-w-0">
                {events.slice(0, CHIP_LIMIT).map((ev) => (
                  <MonthChip
                    key={ev.id}
                    event={ev}
                    canDrag={canDrag}
                    onDragStart={onDragStart}
                    onDragEnd={onDragEnd}
                    onOpenEvent={onOpenEvent}
                  />
                ))}
                {overflow > 0 && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onOpenDay(date); }}
                    className="text-[11px] text-muted hover:text-txt text-left px-1 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-primary/60 transition-colors"
                  >
                    {t("calendar.more", { count: overflow })}
                  </button>
                )}
              </div>

              {events.length > 0 && (
                <div className="sm:hidden flex flex-wrap gap-1 items-center mt-auto">
                  {events.slice(0, 4).map((ev) => (
                    <span
                      key={ev.id}
                      className={`w-1.5 h-1.5 rounded-full ${typeStyle(ev.type).dot} ${ev.completed ? "opacity-40" : ""}`}
                    />
                  ))}
                  {events.length > 4 && (
                    <span className="text-[10px] text-muted leading-none">+{events.length - 4}</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MonthChip({ event, canDrag, onDragStart, onDragEnd, onOpenEvent }) {
  const style = typeStyle(event.type);
  return (
    <button
      type="button"
      data-testid={`cal-event-${event.id}`}
      draggable={canDrag}
      onDragStart={canDrag ? (e) => { e.stopPropagation(); onDragStart(event, 0, e); } : undefined}
      onDragEnd={canDrag ? onDragEnd : undefined}
      onClick={(e) => { e.stopPropagation(); onOpenEvent(event, e.currentTarget); }}
      title={event.subject}
      className={`w-full flex items-center gap-1.5 px-1.5 py-0.5 rounded-sm border text-[11px] leading-tight text-left outline-none
        transition-[background-color,opacity] duration-150 focus-visible:ring-2 ${style.tint} ${style.hover} ${style.ring}
        ${event.completed ? "opacity-55" : ""} ${canDrag ? "cursor-grab active:cursor-grabbing" : ""}`}
    >
      {!event.allDay && (
        <span className="tabular-nums opacity-80 shrink-0">
          {formatTimeShort(event.start, i18n.language)}
        </span>
      )}
      <span className={`truncate ${event.completed ? "line-through" : ""}`}>{event.subject}</span>
    </button>
  );
}
